/**
 * Profile page route - User profile, stats, game history, and account management
 *
 * Protected route requiring authentication. Displays the user's avatar,
 * profile info, aggregated game stats, paginated game history, and
 * account management actions (password reset, delete account).
 */

import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { AvatarUpload } from '~/components/profile/AvatarUpload';
import { DeleteAccountModal } from '~/components/profile/DeleteAccountModal';
import { GameHistory } from '~/components/profile/GameHistory';
import { useGameStats, useProfile } from '~/hooks/useProfile';
import { apiClient } from '~/lib/api';
import { getAccessToken } from '~/lib/auth';
import { requireAuth } from '~/lib/route-guards';

// ============================================================================
// Route Definition
// ============================================================================

export const Route = createFileRoute('/$slug/profile')({
  beforeLoad: ({ context }: { context: Record<string, any> }) => {
    requireAuth(context.slug as string);
  },
  component: ProfilePage,
});

// ============================================================================
// Helpers
// ============================================================================

function formatMemberSince(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

// ============================================================================
// Stat Card
// ============================================================================

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}

function StatCard({ icon, label, value }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col items-center gap-1 flex-1 min-w-0">
      <div className="text-indigo-600">{icon}</div>
      <span className="text-2xl font-bold text-gray-900">{value}</span>
      <span className="text-xs text-gray-500 text-center">{label}</span>
    </div>
  );
}

// ============================================================================
// Profile Page Component
// ============================================================================

function ProfilePage() {
  const router = useRouter();
  const { slug } = Route.useRouteContext() as { slug: string };
  const { data: profile, refetch: refetchProfile } = useProfile();
  const { data: stats } = useGameStats();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [passwordResetStatus, setPasswordResetStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleGoBack = useCallback(() => {
    router.history.back();
  }, [router]);

  const handleAvatarUploadComplete = useCallback(
    (_url: string) => {
      refetchProfile();
    },
    [refetchProfile],
  );

  const handlePasswordReset = useCallback(async () => {
    if (!profile?.email || passwordResetStatus === 'sending') return;

    setPasswordResetStatus('sending');
    try {
      const token = getAccessToken();
      await apiClient('/auth/password/reset-request', {
        method: 'POST',
        body: { email: profile.email },
        token,
      });
      setPasswordResetStatus('sent');
    } catch {
      setPasswordResetStatus('error');
    }
  }, [profile?.email, passwordResetStatus]);

  // Loading state
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-6 w-20 bg-gray-200 rounded" />
            <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col items-center gap-4">
              <div className="h-24 w-24 bg-gray-200 rounded-full" />
              <div className="h-5 w-40 bg-gray-200 rounded" />
              <div className="h-4 w-32 bg-gray-100 rounded" />
            </div>
            <div className="flex gap-3">
              <div className="flex-1 bg-white rounded-xl shadow-sm p-4 h-24" />
              <div className="flex-1 bg-white rounded-xl shadow-sm p-4 h-24" />
              <div className="flex-1 bg-white rounded-xl shadow-sm p-4 h-24" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.displayName || 'User';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8 flex flex-col gap-6">
        {/* Back navigation */}
        <button
          type="button"
          onClick={handleGoBack}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 w-fit min-h-[44px]"
        >
          <svg
            className="h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back
        </button>

        {/* Profile card */}
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col items-center gap-4">
          <AvatarUpload
            currentAvatarUrl={profile.avatarUrl}
            name={fullName}
            email={profile.email}
            onUploadComplete={handleAvatarUploadComplete}
          />
          <div className="text-center">
            <h1 className="text-xl font-bold text-gray-900">{fullName}</h1>
            <p className="text-sm text-gray-500">{profile.email}</p>
            <p className="text-xs text-gray-400 mt-1">
              Member since {formatMemberSince(profile.createdAt)}
            </p>
          </div>
        </div>

        {/* Stats summary */}
        {stats && (
          <div className="flex gap-3">
            <StatCard
              icon={
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                </svg>
              }
              label="Total Points"
              value={stats.totalPoints.toLocaleString()}
            />
            <StatCard
              icon={
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                </svg>
              }
              label="Games Played"
              value={stats.totalGamesPlayed}
            />
            <StatCard
              icon={
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                </svg>
              }
              label="Average Score"
              value={`${Math.round(stats.averagePercentage)}%`}
            />
          </div>
        )}

        {/* Game history */}
        <GameHistory />

        {/* Account actions */}
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Account</h2>

          {/* Password reset */}
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Password</p>
              <p className="text-xs text-gray-500">Send a reset link to your email</p>
            </div>
            <button
              type="button"
              onClick={handlePasswordReset}
              disabled={passwordResetStatus === 'sending' || passwordResetStatus === 'sent'}
              className="rounded-lg px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] whitespace-nowrap"
            >
              {passwordResetStatus === 'sending' && 'Sending...'}
              {passwordResetStatus === 'sent' && 'Email Sent!'}
              {passwordResetStatus === 'error' && 'Try Again'}
              {passwordResetStatus === 'idle' && 'Reset Password'}
            </button>
          </div>

          {passwordResetStatus === 'sent' && (
            <p className="text-sm text-green-600" role="status">
              Password reset email sent! Check your inbox.
            </p>
          )}

          {passwordResetStatus === 'error' && (
            <p className="text-sm text-red-600" role="alert">
              Failed to send reset email. Please try again.
            </p>
          )}

          {/* Divider */}
          <hr className="border-gray-200" />

          {/* Delete account */}
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Delete Account</p>
              <p className="text-xs text-gray-500">Permanently remove your account and data</p>
            </div>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="rounded-lg px-4 py-2 text-sm font-medium border border-red-300 text-red-600 hover:bg-red-50 min-h-[44px] whitespace-nowrap"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete account modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        slug={slug}
      />
    </div>
  );
}
